import clsx from 'clsx';
import type React from 'react';
import { forwardRef, type HTMLAttributes } from 'react';

export type ButtonProps = Omit<HTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  is?: 'button' | 'input' | 'a';
  mode?: 'primary' | 'secondary' | 'custom';
  type?: 'primary' | 'secondary' | 'tertiary' | 'contrast' | 'custom' | 'glassy' | 'error' | 'black';
  size?: 'tiny' | 'small' | 'medium' | 'large';
  block?: boolean;
  loading?: boolean;
  className?: string;
  href?: string;
  externalTab?: boolean;
  onClick?: () => void;
  target?: string;
  children?: React.ReactNode;
  disabled?: boolean;
};

/**
 * Primary UI component for user interaction
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      is = 'button',
      type = 'primary',
      size = 'medium',
      block = false,
      className = '',
      loading = false,
      href = '',
      externalTab = false,
      mode,
      target,
      children,
      ...props
    },
    ref
  ) => {
    const isBywayInternalUrl = (url?: string) => {
      if (!url) return false;
      if (url.startsWith('/')) return true;

      try {
        const parsed = new URL(url);
        return parsed.hostname.endsWith('byway.travel');
      } catch {
        return false;
      }
    };

    // legacy props
    if (externalTab && href && !isBywayInternalUrl(href)) {
      target = '_blank';
    }

    if (mode && !type) {
      type = mode;
    }

    if (className) mode = 'custom';
    const classes = clsx(
      'justify-center items-center rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 typography-button disabled:opacity-50 disabled:cursor-not-allowed',
      className,
      {
        'text-white bg-primary-500 shadow-sm hover:bg-primary-600 focus:ring-primary-500': type === 'primary',
        'text-black bg-white shadow-sm hover:bg-primary-200 border border-primary-500 focus:ring-primary-500':
          type === 'secondary',
        'text-black hover:bg-yellow-200 focus:ring-primary-500': type === 'tertiary',
        'border border-white shadow-sm hover:bg-primary-600': type === 'contrast',
        'border border-neutral-300 shadow-sm bg-neutral-200 bg-opacity-90': type === 'glassy',
        'border border-status-error shadow-sm bg-primary-100 hover:bg-primary-200 text-status-error focus:ring-status-error':
          type === 'error',
        'border border-black bg-black text-white hover:bg-black/80 focus:ring-black': type === 'black',
        'py-2 px-4': size === 'medium',
        'py-2 px-6 text-xl': size === 'large',
        'py-2 px-4 text-sm': size === 'small',
        'py-0.5 px-4 text-sm': size === 'tiny',
        'flex w-full': block,
        'inline-flex': !block
      }
    );

    const innerContent = (
      <>
        {/* Spinner icon inlined short term until UI package deprecated */}
        {loading && (
          <svg
            fill="none"
            viewBox="0 0 24 24"
            width="1em"
            height="1em"
            aria-hidden="true"
            style={{
              marginRight: size !== 'small' ? '0.5rem' : '0.25rem',
              animation: 'spin 1s linear infinite'
            }}
          >
            <defs>
              <style>
                {`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}
              </style>
            </defs>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }} />
            <path
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              style={{ opacity: 0.75 }}
            />
          </svg>
        )}
        {children}
      </>
    );

    // is Link
    if (href) {
      return (
        <a
          className={classes}
          href={href}
          target={target ?? undefined}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {innerContent}
        </a>
      );
    }

    const Component = is ? is : 'button';

    return (
      <Component
        // @ts-expect-error
        ref={ref}
        type={Component === 'button' ? 'button' : 'submit'}
        className={classes}
        {...props}
      >
        {innerContent}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export { Button };
